// Edit planner for CREATE / ALTER / DROP SCHEMA (v2 Slice D5).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D5.
//
// All three ops append a fresh statement after the source statement
// (DSE2-7: no coalescing by default). The source statement kind is
// unrestricted — each op names the target schema directly via its own fields.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { DdlEdit, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeAlterSchema,
  synthesizeCreateSchema,
  synthesizeDropSchema,
} from './dialect.schema';

type SchemaEdit = Extract<
  DdlEdit,
  { op: 'create-schema' | 'alter-schema' | 'drop-schema' }
>;

export function planSchemaEdit(
  ctx: DdlEditCtx,
  edit: SchemaEdit,
): readonly PlannedByteEdit[] {
  const synth = synthForSchema(ctx, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: edit.op,
  }];
}

function synthForSchema(ctx: DdlEditCtx, edit: SchemaEdit): string {
  switch (edit.op) {
    case 'create-schema':
      return synthesizeCreateSchema(ctx.engine, edit.schema);
    case 'alter-schema':
      return synthesizeAlterSchema(
        ctx.engine,
        edit.schemaName,
        edit.quoted ?? false,
        edit.change,
      );
    case 'drop-schema':
      return synthesizeDropSchema(
        ctx.engine,
        edit.schemaName,
        edit.quoted ?? false,
        edit.ifExists ?? false,
        edit.cascade ?? false,
      );
  }
}
